import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import videoService from "../../../services/videoService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const QualityList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [qualities, setQualities] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} qualities`,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
  });

  const getQualities = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.search,
          ...params,
        };

        console.log("Fetching qualities with params:", requestParams);
        const response = await videoService.getAllVideoQuality(requestParams);
        console.log("Qualities API Response:", response);

        if (response.data && response.data.status) {
          const qualitiesData = response.data.data?.data || [];
          const paginationData = response.data.data?.pagination || {};

          console.log("Qualities data:", qualitiesData);
          console.log("Pagination data:", paginationData);

          // Ensure qualitiesData is an array
          if (Array.isArray(qualitiesData)) {
            setQualities(qualitiesData);
            setPagination((prev) => ({
              ...prev,
              total: paginationData.total || qualitiesData.length,
              current: paginationData.currentPage || prev.current,
            }));
          } else {
            console.error("Qualities data is not an array:", qualitiesData);
            setQualities([]);
          }
        } else {
          notify.error({ message: "Failed to fetch video qualities" });
          setQualities([]);
        }
      } catch (error) {
        console.error("Error fetching qualities:", error);
        notify.error({ message: "Failed to fetch video qualities" });
        setQualities([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.search, pagination.pageSize, pagination.current]
  );

  // Load qualities data
  useEffect(() => {
    setPageTitle("Video Quality");
    getQualities();
  }, [getQualities, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getQualities({
      searchText: value,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getQualities({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search,
    });
  };

  // Handle add quality
  const handleAddQuality = () => {
    navigate("/video-quality/add/new");
  };

  // Handle edit quality
  const handleEditQuality = (vqId) => {
    navigate(`/video-quality/edit/${vqId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Quality",
      dataIndex: "quality_name",
      key: "quality_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.quality_name.localeCompare(b.quality_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "vq_is_active",
      key: "vq_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditQuality(record.vq_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Video Qualities ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search qualities..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddQuality}
              size="middle"
            >
              Add Video Quality
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={qualities}
          rowKey="vq_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{
            emptyText: "No video qualities found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default QualityList;
